import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Get_Important_Marketing_Reports_Action } from '../Redux/Actions/MarketingReports_Action';
import { useSidebarWidth } from '../Utility/useSidebarWidth';
import moment from 'moment-timezone';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';

/* ── tokens ─────────────────────────────────────────────────────── */
const ACCENT  = '#0D47A1';
const COLORS  = ['#0D47A1','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4'];

/* ── Custom Bar Tooltip ──────────────────────────────────────────── */
const BarTooltip = ({ active, payload, label, dark }) => {
  if (!active || !payload?.length) return null;
  const bg  = dark ? '#1E1E2E' : '#fff';
  const bdr = dark ? 'rgba(255,255,255,0.08)' : '#E8EAF0';
  return (
    <div style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 12, padding: '12px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)', fontFamily: "'DM Sans',sans-serif" }}>
      <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: dark ? '#94A3B8' : '#7B829A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: ACCENT }}>{payload[0]?.value?.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: dark ? '#94A3B8' : '#7B829A' }}>leads</span></p>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════ */
export const ImportantMarketingReport = () => {
  const dispatch     = useDispatch();
  const theme        = useTheme();
  const dark         = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  /* tokens */
  const bg      = dark ? '#0f1117' : '#F7F8FC';
  const surface = dark ? '#1E1E2E' : '#FFFFFF';
  const border  = dark ? 'rgba(255,255,255,0.07)' : '#E8EAF0';
  const textPri = dark ? '#E2E8F0' : '#1C2340';
  const textSec = dark ? '#94A3B8' : '#7B829A';

  useEffect(() => { dispatch(Get_Important_Marketing_Reports_Action()); }, [dispatch]);

  const resgetdata = useSelector(s => s.AllMarketingReports.LeadPreviewmarketingreport);
  const loading    = useSelector(s => s.AllMarketingReports.loading);

  const {
    leadsByChannal  = [],
    leadsByStage    = [],
    leadsByProject  = [],
    campaignSummary = [],
  } = resgetdata || {};

  /* ── aggregates ── */
  const totalLeads   = campaignSummary.reduce((s, i) => s + (i.leadsNumber  || 0), 0);
  const totalDeals   = campaignSummary.reduce((s, i) => s + (i.dealsNumber  || 0), 0);
  const totalRevenue = campaignSummary.reduce((s, i) => s + (i.totalRevenue || 0), 0);
  const totalCost    = campaignSummary.reduce((s, i) => s + (i.cost         || 0), 0);
  const totalCPL     = totalLeads  ? Math.round(totalCost / totalLeads)  : 0;
  const totalCPA     = totalDeals  ? Math.round(totalCost / totalDeals)  : 0;
  const totalROI     = totalCost   ? Math.round(totalRevenue / totalCost): 0;

  const kpis = [
    { label: 'Leads',    value: totalLeads,   accent: false },
    { label: 'Deals',    value: totalDeals,   accent: true  },
    { label: 'Revenue',  value: `$${totalRevenue.toLocaleString()}`, accent: false },
    { label: 'Cost',     value: `$${totalCost.toLocaleString()}`,    accent: false },
    { label: 'CPL',      value: `$${totalCPL}`,  accent: false },
    { label: 'CPA',      value: `$${totalCPA}`,  accent: false },
    { label: 'ROI',      value: `${totalROI}×`,  accent: false },
  ];

  /* ── table headers ── */
  const tableHeaders = ['#','Campaign','Start Date','Start Time','End Date','End Time','Leads','Deals','Revenue','Cost','CPL','CPA','ROI'];

  /* ── shared chart style ── */
  const chartCard = {
    background: surface, border: `1px solid ${border}`, borderRadius: 20,
    padding: '22px 20px', boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
  };

  /* ── no data / loading ── */
  if (loading || !resgetdata) return (
    <div style={{
      position: 'fixed', top: '50px', left: sidebarWidth, right: 0, bottom: 0,
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: bg, flexDirection: 'column', gap: 16,
    }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${ACCENT}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: textSec, margin: 0 }}>Loading report…</p>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════ */
  return (
    <div style={{
      position: 'fixed',
      top: '50px',
      left: sidebarWidth,
      right: 0,
      bottom: 0,
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      overflowY: 'auto',
      overflowX: 'hidden',
      background: bg,
      fontFamily: "'DM Sans',sans-serif",
    }}>
      <div style={{ padding: '32px 28px 64px' }}>

        {/* ── Page header ── */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: textPri, letterSpacing: '-0.5px' }}>
            Marketing Report
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: textSec }}>
            {campaignSummary.length} campaigns · overall performance snapshot
          </p>
        </div>

        {/* ── KPI Cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 14, marginBottom: 28 }}>
          {kpis.map(({ label, value, accent }) => (
            <div key={label} style={{
              background: accent ? ACCENT : surface,
              border: `1px solid ${accent ? ACCENT : border}`,
              borderRadius: 16, padding: '18px 20px',
              boxShadow: accent ? '0 4px 20px rgba(13,71,161,0.25)' : '0 1px 3px rgba(16,24,40,0.08)',
            }}>
              <p style={{ margin: '0 0 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: accent ? 'rgba(255,255,255,0.65)' : textSec }}>{label}</p>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: accent ? '#fff' : textPri, lineHeight: 1, letterSpacing: '-0.5px' }}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
            </div>
          ))}
        </div>

        {/* ── Charts Row: Channels + Stages ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20, marginBottom: 20 }}>

          {/* Channels */}
          <div style={chartCard}>
            <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: textPri }}>Channels</p>
            <p style={{ margin: '0 0 18px', fontSize: 12, color: textSec }}>Leads per channel</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={leadsByChannal} margin={{ top: 8, right: 8, left: -16, bottom: 8 }} barCategoryGap="35%">
                <CartesianGrid strokeDasharray="3 3" stroke={dark ? 'rgba(255,255,255,0.05)' : '#F0F2F8'} vertical={false} />
                <XAxis dataKey="channalName" tick={{ fontSize: 11, fill: textSec, fontFamily: "'DM Sans',sans-serif" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: textSec }} axisLine={false} tickLine={false} />
                <Tooltip content={<BarTooltip dark={dark} />} cursor={{ fill: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', radius: 8 }} />
                <Bar dataKey="totalLeads" fill={ACCENT} radius={[8, 8, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Stages */}
          <div style={chartCard}>
            <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: textPri }}>Stages</p>
            <p style={{ margin: '0 0 18px', fontSize: 12, color: textSec }}>Lead distribution by stage</p>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={leadsByStage} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={dark ? 'rgba(255,255,255,0.05)' : '#F0F2F8'} />
                <XAxis dataKey="stageName" tick={{ fontSize: 11, fill: textSec, fontFamily: "'DM Sans',sans-serif" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: textSec }} axisLine={false} tickLine={false} />
                <Tooltip content={<BarTooltip dark={dark} />} />
                <Line
                  type="monotone" dataKey="totalLeads" stroke={ACCENT} strokeWidth={2.5}
                  dot={{ r: 4, fill: ACCENT, stroke: surface, strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: ACCENT, stroke: surface, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Projects Chart ── */}
        <div style={{ ...chartCard, marginBottom: 20 }}>
          <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: textPri }}>Projects</p>
          <p style={{ margin: '0 0 18px', fontSize: 12, color: textSec }}>Leads per project</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={leadsByProject} margin={{ top: 8, right: 8, left: -16, bottom: 8 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke={dark ? 'rgba(255,255,255,0.05)' : '#F0F2F8'} vertical={false} />
              <XAxis dataKey="projectName" tick={{ fontSize: 11, fill: textSec, fontFamily: "'DM Sans',sans-serif" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: textSec }} axisLine={false} tickLine={false} />
              <Tooltip content={<BarTooltip dark={dark} />} cursor={{ fill: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', radius: 8 }} />
              <Bar dataKey="totalLeads" fill={COLORS[1]} radius={[8, 8, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ── Campaign Summary Table ── */}
        <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 1px 3px rgba(16,24,40,0.08)' }}>
          {/* table header */}
          <div style={{ padding: '18px 24px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: textPri }}>Campaign Summary</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: textSec }}>{campaignSummary.length} active campaigns</p>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, background: 'rgba(13,71,161,0.08)', padding: '4px 12px', borderRadius: 20 }}>
              {campaignSummary.length} rows
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: dark ? 'rgba(255,255,255,0.03)' : '#F7F8FC' }}>
                  {tableHeaders.map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontSize: 10, fontWeight: 700, color: textSec,
                      letterSpacing: '0.07em', textTransform: 'uppercase',
                      borderBottom: `1px solid ${border}`, whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campaignSummary.map((item, idx) => (
                  <tr key={item.campaignId}
                    style={{ borderBottom: `1px solid ${border}`, transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.03)' : '#F9FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* # */}
                    <td style={{ padding: '12px 16px', fontSize: 12, color: textSec }}>{idx + 1}</td>

                    {/* Campaign Name */}
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: textPri, whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[idx % COLORS.length], flexShrink: 0 }} />
                        {item.campaignName}
                      </div>
                    </td>

                    {/* Start Date */}
                    <td style={{ padding: '12px 16px', fontSize: 12, color: textSec, whiteSpace: 'nowrap' }}>
                      {item.startDate ? moment(item.startDate).tz('Asia/Dubai').format('MMM DD, YYYY') : '—'}
                    </td>

                    {/* Start Time */}
                    <td style={{ padding: '12px 16px', fontSize: 12, color: textSec, whiteSpace: 'nowrap' }}>
                      {item.startDate ? moment(item.startDate).tz('Asia/Dubai').format('hh:mm A') : '—'}
                    </td>

                    {/* End Date */}
                    <td style={{ padding: '12px 16px', fontSize: 12, color: textSec, whiteSpace: 'nowrap' }}>
                      {item.endDate ? moment(item.endDate).tz('Asia/Dubai').format('MMM DD, YYYY') : '—'}
                    </td>

                    {/* End Time */}
                    <td style={{ padding: '12px 16px', fontSize: 12, color: textSec, whiteSpace: 'nowrap' }}>
                      {item.endDate ? moment(item.endDate).tz('Asia/Dubai').format('hh:mm A') : '—'}
                    </td>

                    {/* Leads */}
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: ACCENT }}>
                      {(item.leadsNumber || 0).toLocaleString()}
                    </td>

                    {/* Deals */}
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ display: 'inline-block', fontSize: 13, fontWeight: 700, color: '#10B981', background: 'rgba(16,185,129,0.1)', padding: '2px 10px', borderRadius: 20 }}>
                        {(item.dealsNumber || 0).toLocaleString()}
                      </span>
                    </td>

                    {/* Revenue */}
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: textPri }}>
                      ${(item.totalRevenue || 0).toLocaleString()}
                    </td>

                    {/* Cost */}
                    <td style={{ padding: '12px 16px', fontSize: 13, color: textSec }}>
                      ${(item.cost || 0).toLocaleString()}
                    </td>

                    {/* CPL */}
                    <td style={{ padding: '12px 16px', fontSize: 13, color: textSec }}>
                      ${(item.cpl || 0).toLocaleString()}
                    </td>

                    {/* CPA */}
                    <td style={{ padding: '12px 16px', fontSize: 13, color: textSec }}>
                      ${(item.cpa || 0).toLocaleString()}
                    </td>

                    {/* ROI */}
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        display: 'inline-block', fontSize: 12, fontWeight: 700,
                        color: (item.roi || 0) > 1 ? '#10B981' : '#EF4444',
                        background: (item.roi || 0) > 1 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                        padding: '2px 10px', borderRadius: 20,
                      }}>
                        {item.roi || 0}×
                      </span>
                    </td>
                  </tr>
                ))}

                {/* Totals row */}
                {campaignSummary.length > 0 && (
                  <tr style={{ background: dark ? 'rgba(13,71,161,0.1)' : 'rgba(13,71,161,0.04)', borderTop: `2px solid rgba(13,71,161,0.2)` }}>
                    <td colSpan={6} style={{ padding: '12px 16px', fontSize: 12, fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Totals</td>
                    <td style={{ padding: '12px 16px', fontSize: 15, fontWeight: 800, color: ACCENT }}>{totalLeads.toLocaleString()}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: '#10B981', background: 'rgba(16,185,129,0.1)', padding: '2px 10px', borderRadius: 20 }}>
                        {totalDeals.toLocaleString()}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 800, color: textPri }}>${totalRevenue.toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: textSec }}>${totalCost.toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: textSec }}>${totalCPL.toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: textSec }}>${totalCPA.toLocaleString()}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: ACCENT, background: 'rgba(13,71,161,0.1)', padding: '2px 10px', borderRadius: 20 }}>{totalROI}×</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};
